//@ts-nocheck
import MenuBar from './MenuBar';
import PropTypes from 'prop-types';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Link } from '@tiptap/extension-link';
import { Box, InputLabel, LinearProgress } from '@mui/material';
import { XGButton } from 'xg-cl';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_MENU_ITEMS } from '../../constants/editor';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { useTranslation } from 'react-i18next';
import { useProseSpellCheckLanguage } from '../../hooks/useProseSpellCheckAttributes';
import './Editor.css';

/**
 * Functional React component for rendering a custom editor menu bar.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties.
 * @param {string} [props.content] - The content for the editor component.
 * @param {string} [props.id] - The ID for the editor menu bar component.
 * @param {boolean} [props.isPreviewing] - Boolean value to indicate whether the editor is previewing.
 * @param {string} [props.label] - The label for the editor menu bar component.
 * @param {Function} [props.setDescription] - The set value of the html output for the editor menu bar component.
 * @param {Function} [props.setMarkdown] - The set value of the markdown output for the editor menu bar component.
 * @param {Function} [props.setIsPreviewing] - The set value of the isPreviewing output for the editor.
 * @param {boolean} [props.showActions] - Boolean value to indicate whether the editor actions should be shown.'
 * @param {boolean} [props.editable] - Boolean value to indicate whether the editor should be editable.
 * @param {Function} [props.onCancelAction] - The callback function to be called when the cancel button is clicked.
 * @param {Function} [props.onSaveAction] - The callback function to be called when the save button is clicked.
 * @param {string} [props.placeholder] - The placeholder text for the editor.
 * @param {node} [props.generatePrompt] - The component to render for generating AI content.
 * @param {Object} [props.style] - Custom styles to apply to the editor content.
 * @param {boolean} [props.isEditorDisabled] - Custom styles to disable the content of the Editor.
 * @param {boolean} [props.isEditorGenerating] - Boolean value to indicate whether the editor is generating content.
 * @param {number} [props.maxLength] - Maximum length of text input in the editor.
 * @param {Function} [props.onLengthChange] - Callback function that receives the current text length.
 * @param {Function} [props.onBlurAction] - Callback function to be called when editor loses focus.
 * @param {Object} [props.menuItems] - Object specifying which menu items to show/hide.
 * @param {Object} [props.fontSize] - Object specifying font sizes for different screen sizes.
 * @param {boolean} [props.refreshOnChange] - Boolean value to indicate whether the editor should be refreshed when the content changes.
 *
 * @returns {JSX.Element} React element representing the editor menu bar.
 */

const Editor = ({
  content,
  editable = true,
  isPreviewing = false,
  id,
  label,
  setDescription,
  setMarkdown,
  setIsPreviewing,
  showActions,
  onCancelAction,
  onSaveAction,
  placeholder,
  generatePrompt,
  style,
  isEditorDisabled = false,
  isEditorGenerating = false,
  maxLength,
  onLengthChange,
  onBlurAction,
  menuItems = DEFAULT_MENU_ITEMS,
  fontSize = { xs: '16px', md: '18px' },
  refreshOnChange = true,
  ...props
}) => {
  const { t } = useTranslation(['common', 'trip']);
  const editorRef = useRef(null);
  const spellCheckLanguage = useProseSpellCheckLanguage();
  const editorAttributes = useMemo(
    () => ({
      lang: spellCheckLanguage,
      spellcheck: editable ? 'true' : 'false'
    }),
    [editable, spellCheckLanguage]
  );

  const editor = useEditor({
    content: `${content || ''}`,
    editorProps: {
      attributes: editorAttributes,
      handleKeyDown: (view, event) => {
        // Check if we're at maxLength and user is trying to add more content
        if (maxLength) {
          const currentLength = view.state.doc.textContent.length;
          const isMaxLengthReached = currentLength >= maxLength;

          // Call length change callback if provided
          if (onLengthChange) {
            onLengthChange(currentLength);
          }

          // Allow deletion and navigation keys even at max length
          const isDeleteKey =
            event.key === 'Backspace' || event.key === 'Delete';
          const isNavigationKey =
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'ArrowUp' ||
            event.key === 'ArrowDown' ||
            event.key === 'Home' ||
            event.key === 'End';
          const isModifierKey = event.ctrlKey || event.metaKey;

          if (
            isMaxLengthReached &&
            !isDeleteKey &&
            !isNavigationKey &&
            !isModifierKey &&
            event.key.length === 1
          ) {
            event.preventDefault();
            return true;
          }
        }
        return false;
      }
    },
    editable: editable,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Markdown.configure({
        transformPastedText: true,
        transformCopiedText: true
      }),
      TextAlign.configure({
        types: ['paragraph', 'heading']
      }),
      Placeholder.configure({
        placeholder: `${placeholder || ''}`
      }),
      Link.configure({
        openOnClick: !editable,
        HTMLAttributes: {
          class: 'markdown__link',
          rel: null,
          target: '_blank'
        }
      }),
      CharacterCount.configure({
        limit: maxLength
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell
    ],
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = editor.storage.markdown.getMarkdown();
      const currentLength = editor.state.doc.textContent.length;

      // Call length change callback if provided
      if (onLengthChange) {
        onLengthChange(currentLength);
      }

      // Check maxLength
      if (maxLength && editor.state.doc.textContent.length > maxLength) {
        // Truncate content if it exceeds maxLength
        const transaction = editor.state.tr.deleteRange(
          maxLength,
          editor.state.doc.textContent.length
        );
        editor.view.dispatch(transaction);
        return;
      }

      setDescription?.(html);
      setMarkdown?.(markdown);
    },
    onFocus() {
      if (editable) {
        setIspreviewingEditor(false);
      }
    },
    onBlur() {
      if (typeof onBlurAction === 'function') {
        // Ignore blur events caused by interacting with controls inside this editor
        // (toolbar buttons, heading menu, etc). Only save when focus leaves editor.
        setTimeout(() => {
          const activeElement = document.activeElement;
          const editorRoot = editorRef?.current;
          const isFocusStillInsideEditor =
            !!editorRoot &&
            !!activeElement &&
            editorRoot.contains(activeElement);
          const isFocusInsideEditorPopover =
            !!activeElement &&
            !!activeElement.closest &&
            !!activeElement.closest(
              '.menu-bar__dialog, .menu-bar-heading, #edit-link-popover'
            );
          const isEditorPopoverOpen = !!document.querySelector(
            '.menu-bar__dialog, .menu-bar-heading, #edit-link-popover'
          );

          if (
            !isFocusStillInsideEditor &&
            !isFocusInsideEditorPopover &&
            !isEditorPopoverOpen
          ) {
            onBlurAction();
          }
        }, 0);
      }
    }
  });

  const setIspreviewingEditor = (value = false) => {
    if (typeof setIsPreviewing === 'function') {
      setIsPreviewing(value);
    }
  };

  const onCancel = () => {
    if (typeof onCancelAction === 'function') {
      onCancelAction();
    }

    editor.commands.setContent(content);
    setIspreviewingEditor(true);
  };

  const onSave = async () => {
    if (typeof onSaveAction === 'function') {
      await onSaveAction();
      setIspreviewingEditor(true);
    }
  };

  const getRemainingCharsHint = () => {
    if (maxLength) {
      return maxLength
        ? `${editor.state.doc.textContent.length ?? 0}/${maxLength} ${t('charactersRemaing')}`
        : '';
    }
    return '';
  };

  useEffect(() => {
    if (editor) {
      // Call length change callback with initial content length
      if (onLengthChange && editor.state) {
        onLengthChange(editor.state.doc.textContent.length);
      }
    }
  }, [editor, onLengthChange]);

  useEffect(() => {
    if (editor) {
      editor.setOptions({
        editorProps: {
          ...editor.options.editorProps,
          attributes: {
            ...editor.options.editorProps?.attributes,
            ...editorAttributes
          }
        }
      });
    }
  }, [editor, editorAttributes]);

  useEffect(() => {
    if (editor && refreshOnChange) {
      editor.commands.setContent(content);
    }
  }, [content, editor, refreshOnChange]);

  useEffect(() => {
    if (editor && isPreviewing) {
      editor.commands.blur();
    }
  }, [isPreviewing, editor]);

  useEffect(() => {
    if (editor) {
      editor.commands.setContent(content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Box
      {...props}
      className={`editor ${isPreviewing ? 'text-editor__preview' : ''} ${isPreviewing && editable ? 'text-editor__preview--editable' : ''} ${props?.className || ''} `}
      id={id}
      data-testid='md-editor'
      ref={editorRef}
      sx={{
        pointerEvents: isEditorDisabled || isEditorGenerating ? 'none' : 'auto',
        opacity: isEditorDisabled || isEditorGenerating ? 0.5 : 1
      }}
    >
      {label && (
        <InputLabel
          className='xg-input-label'
          data-testid='xg-input-label'
          sx={{ textAlign: 'left', marginBottom: '8px' }}
          shrink={false}
          htmlFor={id}
        >
          {label}
        </InputLabel>
      )}
      <Box className='text-editor' data-testid='md-text-editor'>
        {!isPreviewing && editable && (
          <>
            <MenuBar
              editor={editor}
              editorRef={editorRef}
              isEditing={!isPreviewing}
              menuItems={menuItems}
            />
          </>
        )}

        {generatePrompt}

        {isEditorGenerating && (
          <Box className='editor-progress'>
            <LinearProgress />
          </Box>
        )}

        <Box sx={{ fontSize }}>
          <EditorContent editor={editor} style={style} />
        </Box>

        {showActions && editable && !isPreviewing && (
          <Box className='editor-buttons'>
            <XGButton
              id='editor-cancel-button'
              data-testid='editor-cancel-button'
              size='medium'
              variant='contained'
              type='secondary'
              text='Cancel'
              onClick={onCancel}
              disabled={isEditorDisabled || isEditorGenerating}
            />
            <XGButton
              id='editor-save-button'
              data-testid='editor-save-button'
              size='medium'
              variant='contained'
              type='primary'
              text='Save'
              onClick={onSave}
              disabled={isEditorDisabled || isEditorGenerating}
            />
          </Box>
        )}
      </Box>
      {maxLength && (
        <div
          className='text-sm-medium text-right text-hint'
          style={{ marginTop: '8px' }}
        >
          {getRemainingCharsHint() || ''}
        </div>
      )}
    </Box>
  );
};

Editor.propTypes = {
  className: PropTypes.string,
  isPreviewing: PropTypes.bool,
  editable: PropTypes.bool,
  content: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  setDescription: PropTypes.func,
  setMarkdown: PropTypes.func,
  setIsPreviewing: PropTypes.func,
  showActions: PropTypes.bool,
  onCancelAction: PropTypes.func,
  onSaveAction: PropTypes.func,
  placeholder: PropTypes.string,
  generatePrompt: PropTypes.node,
  style: PropTypes.object,
  isEditorDisabled: PropTypes.bool,
  isEditorGenerating: PropTypes.bool,
  maxLength: PropTypes.number,
  onLengthChange: PropTypes.func,
  onBlurAction: PropTypes.func,
  menuItems: PropTypes.object,
  fontSize: PropTypes.object,
  refreshOnChange: PropTypes.bool
};

export default Editor;
